import React, { memo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { GeoJsonProperties } from '@trimblemaps/trimblemaps-js/geojson'
import TrimbleMap from 'transfix-maps/components/TrimbleMap'
import { IUseMapProps } from 'transfix-maps/hooks'
import { makeStyles } from 'transfix-ui/core/styles'
import { rogersFontFamily } from 'transfix-ui/theme'
import { brandColors } from 'transfix-ui/theme/colors'
import { Maybe } from 'types/FMSGraphqlTypes'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { IActiveShipmentFragment } from '../../graphql/ActiveShipmentFragment'
import { getActiveShipmentsMapPositions } from '../../utils'
import ActiveShipmentMapMarker from '../ActiveShipmentMapMarker'
import ActiveShipmentsMapEmpty from '../ActiveShipmentsMapEmpty'

const useActiveShipmentsMapStyles = makeStyles(() => ({
  root: {
    height: '100%',
    overflow: 'hidden',
  },
  trimbleMap: {
    border: '0px',
    '& .trimblemaps-popup-content': {
      fontFamily: rogersFontFamily,
      padding: '0px',
      width: '250px',
    },
    '& .trimblemaps-popup-close-button': {
      color: brandColors.coolGray5,
    },
  },
}))

export interface IActiveShipmentsMapProps {
  activeShipments: IActiveShipmentFragment[]
}

const ActiveShipmentsMap = memo<IActiveShipmentsMapProps>(({ activeShipments }) => {
  const classes = useActiveShipmentsMapStyles()
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()

  const positions = getActiveShipmentsMapPositions({ activeShipments })

  const getPositionPopupHandler = useCallback((selectedPosition: GeoJsonProperties) => {
    const { data } = selectedPosition || {}
    const shipment = JSON.parse(data) as IActiveShipmentFragment
    return <ActiveShipmentMapMarker {...shipment} />
  }, [])

  const popupLinkClickHandler = useCallback(
    (id: string, marketplaceId: Maybe<string>) => {
      trackEvent('Homepage', 'HOME_LOAD_SUMMARY_CLICKED_MAP_PIN_LINK', {
        shipmentId: marketplaceId,
      })
      history.push(`/home/${id}/details`)
    },
    [history, trackEvent]
  )

  const pinClickHandler: IUseMapProps['onSelectPosition'] = useCallback(
    (_, position) => {
      trackEvent('Homepage', 'HOME_LOAD_SUMMARY_CLICKED_MAP_PIN', { position })

      const { data } = position || {}

      if (data) {
        const shipment = data as IActiveShipmentFragment
        const { id, marketplaceId } = shipment || {}
        document
          .getElementById(`view-details-link-${id}`)
          ?.addEventListener('click', () => popupLinkClickHandler(id, marketplaceId))
      }
    },
    [popupLinkClickHandler, trackEvent]
  )

  return (
    <div className={classes.root}>
      {activeShipments.length === 0 && <ActiveShipmentsMapEmpty />}
      <TrimbleMap
        className={classes.trimbleMap}
        positions={positions}
        getPositionPopup={getPositionPopupHandler}
        onSelectPosition={pinClickHandler}
        fitBoundsOptions={{
          maxZoom: 3,
        }}
      />
    </div>
  )
})

export default ActiveShipmentsMap
